<h2 mat-dialog-title>
  <span>{{ isEditMode ? "Edit" : "Add" }} Protection Type</span>
  <div class="subtitle">
    {{
      isEditMode
        ? "Edit the protection type details for this merchant."
        : "Add a new protection type to your system."
    }}
  </div>
</h2>

<mat-dialog-content [formGroup]="form">
  <div class="pv-xs" fxLayout="column" fxLayoutGap="8px">
    <mat-form-field appearance="outline">
      <mat-label>Protection Type</mat-label>
      <mat-select formControlName="type" placeholder="Select protection type">
        <mat-option *ngFor="let type of protectionTypes" [value]="type.id">
          {{ type.name }}
        </mat-option>
      </mat-select>
      <mat-error *ngIf="form.get('type').hasError('required')">
        Protection type is required
      </mat-error>
    </mat-form-field>

    <div *ngIf="form.get('type').value" class="mb-md">
      <label class="fs-sm fw-semi-b mb-xs d-block">Linked Insurer(s)</label>
      <mat-radio-group
        formControlName="insurers"
        fxLayout="row wrap"
        fxLayoutGap="16px"
      >
        <mat-radio-button *ngFor="let insurer of insurers" [value]="insurer.id">
          {{ insurer.name }}
        </mat-radio-button>
      </mat-radio-group>
      <mat-error
        *ngIf="form.get('insurers').invalid && form.get('insurers').touched"
        class="fs-xs mt-xs"
      >
        Linked insurer is required
      </mat-error>
    </div>

    <mat-form-field appearance="outline">
      <mat-label>Protection Title</mat-label>
      <textarea
        formControlName="title"
        matInput
        placeholder="Enter protection title"
        rows="5"
      ></textarea>
      <mat-error *ngIf="form.get('title').hasError('required')">
        Protection title is required
      </mat-error>
    </mat-form-field>

    <mat-form-field appearance="outline">
      <mat-label>Master Policy No.</mat-label>
      <input
        formControlName="masterPolicyNo"
        matInput
        placeholder="Enter the Master Policy Number"
      />
      <mat-error *ngIf="form.get('masterPolicyNo').hasError('required')">
        Master Policy Number is required
      </mat-error>
    </mat-form-field>

    <div class="email-input" fxLayout="column">
      <div class="fs-sm fw-semi-b mb-xs d-block">Email to Insurer</div>
      <mat-form-field appearance="outline">
        <mat-label>To</mat-label>
        <mat-chip-list #toChipList>
          <mat-chip
            (removed)="removeEmail(i, 'to')"
            *ngFor="let email of toEmails.value; let i = index"
            [class.invalid-email-chip]="isEmailInvalid(i, 'to')"
            [removable]="true"
          >
            {{ email }}
            <button matChipRemove>
              <mat-icon>cancel</mat-icon>
            </button>
          </mat-chip>
          <input
            (blur)="markEmailsTouched('to')"
            (matChipInputTokenEnd)="addEmail($event, 'to')"
            [matChipInputAddOnBlur]="true"
            [matChipInputFor]="toChipList"
            [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
            placeholder="Add email here"
          />
        </mat-chip-list>
      </mat-form-field>
      <mat-error
        *ngIf="toEmails.touched && toEmails.hasError('minValidEmails')"
        class="fs-xs mt-xs mb-xs"
      >
        At least one valid email address is required
      </mat-error>
      <mat-error
        *ngIf="
          toEmails.touched &&
          !toEmails.hasError('minValidEmails') &&
          hasInvalidEmail('to')
        "
        class="fs-xs mt-xs mb-xs"
      >
        Remove or correct the highlighted email address
      </mat-error>

      <mat-form-field appearance="outline">
        <mat-label>Cc</mat-label>
        <mat-chip-list #ccChipList>
          <mat-chip
            (removed)="removeEmail(i, 'cc')"
            *ngFor="let email of ccEmails.value; let i = index"
            [class.invalid-email-chip]="isEmailInvalid(i, 'cc')"
            [removable]="true"
          >
            {{ email }}
            <button matChipRemove>
              <mat-icon>cancel</mat-icon>
            </button>
          </mat-chip>
          <input
            (blur)="markEmailsTouched('cc')"
            (matChipInputTokenEnd)="addEmail($event, 'cc')"
            [matChipInputAddOnBlur]="true"
            [matChipInputFor]="ccChipList"
            [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
            placeholder="Add email here"
          />
        </mat-chip-list>
      </mat-form-field>
      <mat-error
        *ngIf="ccEmails.touched && hasInvalidEmail('cc')"
        class="fs-xs mt-xs mb-xs"
      >
        Remove or correct the highlighted email address
      </mat-error>
    </div>

    <div class="mb-md">
      <label class="fs-sm fw-semi-b mb-xs d-block"
        >Upload Protection Catalog</label
      >
      <app-resizable-upload
        [accept]="'.png,.jpg,.jpeg,.pdf'"
        [maxSize]="5"
        [multiple]="true"
        [requireCropping]="false"
        formControlName="catalog"
      ></app-resizable-upload>
      <mat-error
        *ngIf="
          form.get('catalog').hasError('required') &&
          (form.get('catalog').touched || form.get('catalog').dirty)
        "
        class="fs-xs mt-xs"
      >
        Catalog upload is required
      </mat-error>
      <mat-error
        *ngIf="
          form.get('catalog').hasError('fileSize') &&
          (form.get('catalog').touched || form.get('catalog').dirty)
        "
        class="fs-xs mt-xs"
      >
        File size must not exceed 5MB
      </mat-error>

      <div
        *ngIf="existingCatalogs.length"
        class="pv-xs"
        fxLayout="column"
        fxLayoutGap="8px"
      >
        <app-document-card
          (toggleDelete)="toggleDeletion($event)"
          *ngFor="let doc of existingCatalogs"
          [document]="doc"
          [showViewButton]="false"
        ></app-document-card>
      </div>
    </div>

    <div class="mb-md">
      <label class="fs-sm fw-semi-b mb-xs d-block"
        >Protection Description</label
      >
      <div class="NgxEditor__Wrapper">
        <ngx-editor-menu
          [customMenuRef]="accordionMenu"
          [editor]="editor"
          [toolbar]="toolbar"
        ></ngx-editor-menu>
        <ng-template #accordionMenu>
          <div class="NgxEditor__Seperator"></div>
          <div
            (mousedown)="$event.preventDefault(); onToggleAccordion()"
            [title]="
              isAccordionActive
                ? 'Remove accordion (keeps its content)'
                : 'Accordion'
            "
            class="NgxEditor__MenuItem NgxEditor__MenuItem--Icon"
          >
            <div
              [class.NgxEditor__MenuItem--Active]="isAccordionActive"
              class="NgxEditor__MenuItem--IconContainer"
            >
              <svg
                fill="currentColor"
                height="20"
                viewBox="0 0 24 24"
                width="20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 4h18v2H3V4zm0 4h18v5H3V8zm2 2v1h14v-1H5zm-2 5h18v2H3v-2zm0 4h18v2H3v-2z"
                />
              </svg>
            </div>
          </div>
        </ng-template>
        <ngx-editor
          [editor]="editor"
          formControlName="description"
          placeholder="Type protection description here..."
        ></ngx-editor>
      </div>
      <mat-error
        *ngIf="
          form.get('description').invalid &&
          (form.get('description').touched || form.get('description').dirty)
        "
        class="fs-xs mt-xs"
      >
        Description is required
      </mat-error>
    </div>
  </div>
</mat-dialog-content>

<mat-dialog-actions align="end">
  <button
    (click)="onPreview()"
    [disabled]="!canPreview"
    class="preview-button"
    mat-stroked-button
    type="button"
  >
    <mat-icon>visibility</mat-icon>
    Preview
  </button>
  <button (click)="onCancel()" mat-button>Cancel</button>
  <button
    (click)="onSave()"
    [disabled]="form.invalid || (isEditMode && !isFormChanged)"
    color="primary"
    mat-raised-button
  >
    {{ isEditMode ? "Update" : "Add" }} Protection
  </button>
</mat-dialog-actions>
